import { useEffect, useState } from 'react';
import { buildSmsHref } from './onboarding-phone';
import { getOs, smsDeepLinkUnsupported } from './ua-detect';
import { track } from './track';
import { browserTimezone } from './timezone';

/**
 * The ONE door, as machinery — shared by every bundle that draws it.
 *
 * There is exactly one way into an Ember account: you text Ember once, from
 * your own phone, and the handle that message arrives from IS the account.
 * Registering and signing in are the same act, which is why there is no
 * password, no emailed link and (since 2026-08-31) no code we send you.
 *
 * The FLOW lived twice — `MessagesDoor` in the marketing bundle and
 * `useInboundSmsDoor` in the app SPA — and the two had drifted: one asked for
 * the consents on every open while the other asked once per browser, one
 * detected a dead `sms:` link and offered a QR to finish on a phone while the
 * other left you staring at a code, and neither survived the person navigating
 * away. Same door, two mechanisms, two sets of bugs. This module is the flow,
 * once; each bundle keeps only its own chrome (marketing's DoorFrame dialog,
 * the SPA's bottom sheet).
 *
 * Three things it owns that no single page can:
 *
 *   1. WAITING. The person leaves for Messages and comes back — to the same
 *      page, reloaded or restored from the back/forward cache, in either
 *      bundle. The wait is therefore the browser's, not a component's: one
 *      record in localStorage, one poller, and a full page load to `returnTo`
 *      the moment their message lands (GET /api/onboarding/:id/status answers
 *      'completed' AND sets the ori_session cookie on that very response).
 *      The wait belongs to the PAGE that opened it, though (2026-09-03 拍板):
 *      the record carries the pathname it began on, and moving to any other
 *      page — an SPA route change, a marketing document load — ends it. Until
 *      then it followed the person site-wide (2026-08-31), and a pill saying
 *      "Waiting for your text" trailed them through pages that had nothing to
 *      do with signing in. Coming back from Messages never counts as moving:
 *      the composer returns you to the page you left. Both bundles still read
 *      and write the one key, so whichever bundle serves the next page can
 *      resume or end the wait correctly.
 *
 *   2. REACH. After firing `sms:` nothing tells us whether Messages opened, so
 *      it is inferred: the tab backgrounding is the app launching. Still
 *      foregrounded a beat later means the link did nothing — desktop, or a
 *      blocked in-app webview — and the flow moves to the phone panel (a QR to
 *      /j/<session>, which reopens the same handoff on a device that can text).
 *
 *   3. CONSENT. 18-or-older and the Terms are asked once per browser, not once
 *      per door: the record lives on the visitor row (migration 135) and
 *      GET /api/visitor/consent reads it. The refusal stays server-side, so a
 *      client that lies about having consent gains nothing.
 *
 * What each bundle still passes in, because only it knows: `surface` (which
 * completion the inbound webhook runs — see the note on OpenDoorInput),
 * `extras` (its own captured attribution / gift code / 投稿 channel), `arrival`
 * (which entrance this is, for `login.*`), and `returnTo`.
 */

const STORAGE_KEY = 'ember_inbound_wait';

/**
 * Fired on `window` the moment this browser's session is known to have
 * changed under the running document — the status poll answered 'completed'
 * (the cookie is on that response), or /start refused because the browser was
 * already signed in. Whoever holds the session state (the SPA's
 * SessionProvider, the marketing DoorProvider) re-reads /api/me on it.
 *
 * It exists because the hard navigation below is not guaranteed to happen.
 * 2026-09-08, prod: on the Facebook Android in-app browser (FB4A) a person came
 * back from Messages, the poll got 'completed' with the cookie, the wait was
 * cleared — and `window.location.replace` did nothing. No page load, so the
 * SPA never re-read /api/me and stood there anonymous with the door gone;
 * every re-tap of the door then hit /start with a live cookie. The iPhone
 * in-app browser walked the same path and reloaded fine. The navigation stays
 * (a full load is still the cleanest way in); this is the floor under it.
 */
export const SESSION_CHANGED_EVENT = 'ember:session-changed';

function announceSessionChanged(): void {
  if (typeof window === 'undefined') return;
  try {
    window.dispatchEvent(new Event(SESSION_CHANGED_EVENT));
  } catch {
    /* an old webview without the Event constructor still gets the navigation */
  }
}

/** How often the browser asks whether the text has landed. */
const POLL_MS = 3000;

/** How long to wait for the tab to background before deciding `sms:` no-op'd.
 *  Long enough to clear an iOS "Open in Messages?" confirm prompt. */
const REACH_WATCH_MS = 2200;

/** How long /start may take before the tap is given back (2026-10-21 field
 *  report: a phone whose network stalled 42s left the button on Preparing…
 *  with no word, then raised the code sheet and fired sms: after the person
 *  had left the page and come back). /start answers in well under a second;
 *  a re-tap reuses the client token, so it gets the same lease and code. */
const START_TIMEOUT_MS = 15_000;

/** The code's server-side TTL, used only when /start's `expires_at` is unreadable. */
const CODE_TTL_FALLBACK_MS = 10 * 60 * 1000;

// (2026-08-31) A stored record whose code already died is never resumed:
// raising a dead sign-in over whatever page the person came back to was a
// worse greeting than silence, whatever the age of the record. Expiry only
// gets a surface while the person is actually mid-wait on a live page.

/** Did the `sms:` handoff reach an app? 'phone' = finish on their phone. */
export type DoorReach = 'pending' | 'opened' | 'phone';

export interface InboundDoorRecord {
  /** The onboarding session /start leased. The poller's whole subject. */
  sessionId: string;
  /** The 6-digit code inside the prefilled message. */
  code: string;
  /** The Ember line the message goes to. */
  toNumber: string;
  /** /start's exact composed text — the inbound handler matches on it. */
  message: string;
  /** Where the browser lands once the text arrives. */
  returnTo: string;
  /**
   * The pathname the door was opened on. The wait lives on this page only:
   * resumed when a load lands here again, ended when the person moves to any
   * other page (2026-09-03). '' on records written before the field existed —
   * those are never held to a page.
   */
  page: string;
  /** Which entrance opened this ('auth', 'register_cta', 'start', 'nav', …). */
  arrival: string;
  /**
   * The completion this lease was started under. Carried so a restart cannot
   * quietly switch it: the record outlives the page that made it, and a wait
   * begun by one bundle's door can be picked up by the other's (the storage is
   * shared) — re-leasing under the local door's surface would change what
   * account the same person ends up with.
   */
  surface: 'me' | 'collect';
  /** Epoch ms after which the code cannot be claimed at all. */
  expiresAt: number;
  /** Only set when a bundle is served from a different origin than the edge. */
  apiBase: string;
  reach: DoorReach;
  /** Epoch ms the code was in hand and the handoff fired — the wait's clock. 0 on a record written before 2026-11-02. */
  readyAt: number;
}

export interface InboundDoorState {
  /** The text we are waiting for, or null when nothing is pending. */
  pending: InboundDoorRecord | null;
  /** A lease is in flight — the tap has been taken, nothing has opened yet. */
  arming: boolean;
  /** The code died before the message arrived; a fresh one is one tap away. */
  expired: boolean;
  /** The person closed the wait. The listening itself continues. */
  minimized: boolean;
  /** Live view of the pending record's reach ('pending' when nothing is up). */
  reach: DoorReach;
}

const EMPTY: InboundDoorState = {
  pending: null, arming: false, expired: false, minimized: false, reach: 'pending',
};

let state: InboundDoorState = EMPTY;
const listeners = new Set<() => void>();
let timer = 0;
let resumed = false;
let clientToken = '';
let clearReachWatch: (() => void) | null = null;

/**
 * What the page did while a text was awaited (2026-11-02 founder: 发短信那一步一半人没走完，
 * 前后端全部记下来). Of the people who take a code, about half never text it, and nothing said
 * what happened between the sms: link and the text that never came: whether the tab left the
 * screen (Messages opening), how long it stayed away, whether it came back, was closed, or was
 * reloaded onto a wait already dead. Each change is one login.wait_lifecycle row carrying the
 * wait so far; the counts below are this document's — a reload starts them again.
 */
interface WaitTrace {
  polls: number;
  pollErrors: number;
  /** The last /status answer: its status, http_<code> when it had none, or network. */
  lastPoll: string;
  hides: number;
  hiddenAt: number;
  hiddenMs: number;
}

const freshTrace = (): WaitTrace => ({ polls: 0, pollErrors: 0, lastPoll: '', hides: 0, hiddenAt: 0, hiddenMs: 0 });
let trace: WaitTrace = freshTrace();
let lifecycleArmed = false;

/** The wait so far, as the props every wait row carries. */
function waitProps(record: InboundDoorRecord): Record<string, string | number | boolean | undefined> {
  return {
    arrival: record.arrival,
    since_ready_ms: record.readyAt ? Date.now() - record.readyAt : undefined,
    reach: record.reach,
    hides: trace.hides,
    hidden_ms: trace.hiddenMs,
    polls: trace.polls,
    poll_errors: trace.pollErrors,
    last_poll: trace.lastPoll || undefined,
    expired: state.expired,
  };
}

function trackWait(state_: string, record: InboundDoorRecord, extra: Record<string, string | number | boolean> = {}): void {
  track('login.wait_lifecycle', { state: state_, ...waitProps(record), ...extra });
}

/** Listen for the page leaving and coming back, once per document; every handler is a no-op with no wait up. */
function armLifecycle(): void {
  if (lifecycleArmed || typeof window === 'undefined') return;
  lifecycleArmed = true;
  document.addEventListener('visibilitychange', () => {
    const record = state.pending;
    if (!record) return;
    if (document.visibilityState === 'hidden') {
      trace.hides += 1;
      trace.hiddenAt = Date.now();
      trackWait('hidden', record);
      return;
    }
    const away = trace.hiddenAt ? Date.now() - trace.hiddenAt : 0;
    trace.hiddenMs += away;
    trace.hiddenAt = 0;
    trackWait('visible', record, trace.hides ? { away_ms: away } : {});
  });
  // Some in-app webviews never report hidden when the app behind them goes to
  // Messages; the window losing focus is the one sign they give (the reach
  // watcher reads it the same way). Only recorded when visibility said nothing.
  let blurred = false;
  window.addEventListener('blur', () => {
    const record = state.pending;
    if (!record || blurred || document.visibilityState === 'hidden') return;
    blurred = true;
    trackWait('blurred', record);
  });
  window.addEventListener('focus', () => {
    const record = state.pending;
    if (!record || !blurred) return;
    blurred = false;
    trackWait('focused', record);
  });
  window.addEventListener('pagehide', (event) => {
    const record = state.pending;
    if (record) trackWait('unloaded', record, { persisted: Boolean((event as PageTransitionEvent).persisted) });
  });
  window.addEventListener('pageshow', (event) => {
    const record = state.pending;
    if (record && (event as PageTransitionEvent).persisted) trackWait('restored', record);
  });
}

function emit(): void {
  for (const listener of listeners) listener();
}

function setState(next: Partial<InboundDoorState>): void {
  state = { ...state, ...next };
  emit();
}

/** /start's idempotency key. crypto.randomUUID is not everywhere (older
 *  in-app webviews, insecure contexts) and throwing here would kill the tap. */
function newClientToken(): string {
  try {
    return crypto.randomUUID();
  } catch {
    return `ct_${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
  }
}

function persist(record: InboundDoorRecord | null): void {
  try {
    if (record) localStorage.setItem(STORAGE_KEY, JSON.stringify(record));
    else localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* A browser that refuses storage still waits — it just cannot survive a
       reload, which is exactly where both doors stood before this existed. */
  }
}

function readStored(): InboundDoorRecord | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<InboundDoorRecord>;
    if (!parsed?.sessionId) return null;
    const expiresAt = Number(parsed.expiresAt);
    return {
      sessionId: String(parsed.sessionId),
      code: String(parsed.code || ''),
      toNumber: String(parsed.toNumber || ''),
      message: String(parsed.message || ''),
      returnTo: String(parsed.returnTo || '/'),
      page: String(parsed.page || ''),
      arrival: String(parsed.arrival || 'nav'),
      surface: parsed.surface === 'collect' ? 'collect' : 'me',
      expiresAt: Number.isFinite(expiresAt) ? expiresAt : 0,
      apiBase: String(parsed.apiBase || ''),
      // Coming back to the page IS the tab having backgrounded, so a resumed
      // wait is 'opened' — except a desktop one, which never had an app to
      // open and must keep its phone panel.
      reach: parsed.reach === 'phone' ? 'phone' : 'opened',
      readyAt: Number.isFinite(Number(parsed.readyAt)) ? Number(parsed.readyAt) : 0,
    };
  } catch {
    return null;
  }
}

/** The pathname of the document on screen ('' outside a browser). */
function currentPage(): string {
  return typeof window === 'undefined' ? '' : window.location.pathname;
}

/** Has the person moved off the page this wait was opened on? */
function leftPage(record: InboundDoorRecord, pathname: string): boolean {
  return Boolean(record.page) && record.page !== pathname;
}

function stopPolling(): void {
  if (!timer) return;
  window.clearTimeout(timer);
  timer = 0;
}

function schedule(delay = POLL_MS): void {
  stopPolling();
  timer = window.setTimeout(() => { void poll(); }, delay);
}

function expire(record: InboundDoorRecord): void {
  stopPolling();
  // The same declared fact the marketing door has always reported: a lease
  // that aged out before any text arrived — with the wait it ended (2026-11-02).
  // Said only on the server's word (last_poll: expired, or session_not_found).
  const { arrival: _arrival, expired: _expired, ...wait } = waitProps(record);
  track('login.handoff_expired', { arrival: record.arrival, ...wait });
  // The record stays: the one action from here mints a fresh code, and
  // throwing the dead one away first would take the explanation with it.
  // Minimized stays as the person left it — a wait they tucked into the pill
  // flips its label to the expired copy there, it doesn't leap back open.
  setState({ expired: true });
}

type StatusAnswer = {
  res: Response;
  data: { status?: string; ads_event_id?: string; error?: string } | null;
};

/** One read of the session's status, counted on the wait's trace; null when the network did not answer. */
async function readStatus(record: InboundDoorRecord): Promise<StatusAnswer | null> {
  try {
    trace.polls += 1;
    const res = await fetch(`${record.apiBase}/api/onboarding/${encodeURIComponent(record.sessionId)}/status`, {
      credentials: 'include',
      headers: { Accept: 'application/json' },
    });
    const data = (await res.json().catch(() => null)) as StatusAnswer['data'];
    trace.lastPoll = data?.status || data?.error || `http_${res.status}`;
    if (!res.ok) trace.pollErrors += 1;
    return { res, data };
  } catch {
    trace.pollErrors += 1;
    trace.lastPoll = 'network';
    return null;
  }
}

/**
 * Act on the server's answer. It is the one judge of how a wait ends
 * (2026-11-02): until then the page ended it at the code's deadline by its own
 * clock, without asking — and 10 people 10-20 → 11-02 came back from Messages
 * after their text had already made their account, and were told the code had
 * expired. The server says expired once the deadline passed unclaimed, and not
 * found once the session is gone (torn down, or completed longer ago than it
 * keeps them). 'done': signed in, or refused as a closed account — this page
 * moves on. 'dead': the code is gone. 'waiting': no answer yet.
 */
function settleFromStatus(record: InboundDoorRecord, answer: StatusAnswer | null): 'done' | 'dead' | 'waiting' {
  if (!answer) return 'waiting';
  const { res, data } = answer;
  // The text came from an account Oriscen closed (2026-10-21 Riley): the
  // proof held, no session was issued. The wait ends here and the sign-in
  // page says the account is closed — never a registration.
  if (res.status === 403 && data?.error === 'account_closed') {
    stopPolling();
    track('login.failed', { arrival: record.arrival, step: 'wait', reason: 'account_closed' });
    persist(null);
    setState({ ...EMPTY });
    window.location.replace(`${record.apiBase}/auth?account=closed`);
    return 'done';
  }
  if (data?.status === 'completed') {
    stopPolling();
    // A fresh registration (ads_event_id present, absent on a plain sign-in)
    // fires the browser half of CompleteRegistration here, once, whichever
    // page happened to be on screen when the text landed.
    trackRegistrationConversion(data.ads_event_id);
    track('login.signed_in', {
      arrival: record.arrival,
      registered: Boolean(data.ads_event_id),
    });
    persist(null);
    setState({ ...EMPTY });
    // The cookie is already on this browser: say so to whoever holds the
    // session state, BEFORE the navigation — which some in-app webviews
    // never perform (see SESSION_CHANGED_EVENT).
    announceSessionChanged();
    // Hard navigation on purpose: the status response set the ori_session
    // cookie, and a full load is what turns this browser signed-in.
    window.location.replace(record.returnTo || '/');
    return 'done';
  }
  if (data?.status === 'expired' || res.status === 404) return 'dead';
  return 'waiting';
}

/**
 * One round of "has it landed yet?".
 *
 * A transient failure is not an answer: the next tick retries. Only the
 * server's word ends the wait — settleFromStatus.
 */
async function poll(): Promise<void> {
  const record = state.pending;
  if (!record || state.expired) return;
  const answer = await readStatus(record);
  if (state.pending?.sessionId !== record.sessionId) return;
  const outcome = settleFromStatus(record, answer);
  if (outcome === 'done') return;
  if (outcome === 'dead') {
    expire(record);
    return;
  }
  schedule();
}

/**
 * Decide, after firing the deep link, whether Messages actually opened. The
 * tab backgrounding (visibilitychange / pagehide / blur) is the app launching;
 * still being foregrounded after REACH_WATCH_MS means the link did nothing.
 */
function watchReach(arrival: string): void {
  clearReachWatch?.();
  let settled = false;
  const settle = (next: DoorReach): void => {
    if (settled) return;
    settled = true;
    if (state.pending) {
      const record = { ...state.pending, reach: next };
      persist(record);
      setState({ pending: record, reach: next });
    }
    if (next === 'phone') track('login.handoff_fallback', { arrival, reason: 'no_launch' });
    cleanup();
  };
  const onHidden = (): void => { if (document.visibilityState === 'hidden') settle('opened'); };
  const onAway = (): void => settle('opened');
  const deadline = window.setTimeout(() => settle('phone'), REACH_WATCH_MS);
  function cleanup(): void {
    window.clearTimeout(deadline);
    document.removeEventListener('visibilitychange', onHidden);
    window.removeEventListener('pagehide', onAway);
    window.removeEventListener('blur', onAway);
    clearReachWatch = null;
  }
  document.addEventListener('visibilitychange', onHidden);
  window.addEventListener('pagehide', onAway);
  window.addEventListener('blur', onAway);
  clearReachWatch = cleanup;
}

export interface OpenDoorInput {
  /** Which entrance this is, for every `login.*` beacon. */
  arrival: string;
  /**
   * Which completion the inbound webhook runs when the text arrives.
   *
   * NOT cosmetic, and not yet unified (2026-08-31 拍板: left standing, written
   * down instead — docs/login-and-registration-doors.md). 'collect' writes a
   * story registration and sends the welcome text; 'me' completes the plain
   * onboarding and sends neither. Whichever a door passes is what the account
   * it creates will be.
   */
  surface: 'me' | 'collect';
  /** Where the browser lands once the text arrives. */
  returnTo: string;
  /**
   * The ISO moment the person ticked 18+ and the Terms — only when they just
   * ticked. Pass '' when the boxes were not drawn because this browser's
   * consent record already answers for them: the server reads that record and
   * keeps its original acceptance moment.
   */
  tosAcceptedAt: string;
  /** Extra /start body fields the host owns (attribution, gift code, 投稿). */
  extras?: Record<string, unknown>;
  /** Only set when a bundle is served from a different origin than the edge. */
  apiBase?: string;
}

export type OpenDoorResult =
  | { ok: true; reach: DoorReach }
  | { ok: false; code: string };

/** A code leased and not yet handed to Messages: what /start answered, and the door it was leased for. */
interface LeasedCode {
  sessionId: string;
  code: string;
  toNumber: string;
  message: string;
  /** Epoch ms after which the code cannot be claimed. */
  expiresAt: number;
  input: OpenDoorInput;
}

type LeaseResult = { ok: true; lease: LeasedCode } | { ok: false; code: string };

/**
 * The code made ready before the tap (2026-11-04 founder: 不加新屏，让用户本来就要点的那一下直接打开短信).
 * An `sms:` link the page fires by itself after /start answers is outside the person's tap, and Facebook's
 * Android browser met it with its own "you're about to leave the app" dialog — Continue there often opened
 * nothing (founder's phone, five runs on 11-02); the same link fired inside a tap went straight to Messages.
 * The 11-02 answer, a second screen whose button fired it, lost the quarter who closed that screen (11-02 →
 * 11-04: 10 of 41 never tapped it, none registered; 25 of the 31 who did registered). So the code is leased
 * while the person is one tap from sending it — a Join that says beside it what happens, for a browser whose
 * consent is on file; the sheet open; or both boxes ticked — and their tap hands it to Messages with nothing
 * awaited in between. Kept usable: a minute and a half before it runs out, a page on screen leases the next.
 */
let prepared: LeasedCode | null = null;
let preparing: Promise<LeaseResult> | null = null;
let renewTimer = 0;
let renewArmed = false;
/** A code handed to Messages needs this long left: the person still has to send it. */
const USABLE_MS = 60_000;
/** How long before a ready code runs out the page leases the next one. */
const RENEW_AHEAD_MS = 90_000;

const readyCode = (input: OpenDoorInput): LeasedCode | null =>
  (prepared && prepared.input.surface === input.surface && prepared.expiresAt - Date.now() > USABLE_MS ? prepared : null);

/**
 * POST /api/onboarding/start: a line + code for this browser (the guest cookie
 * rides the request, so the inbound handler claims THIS visitor). One client
 * token per lease — asking again with it brings the same lease back instead of
 * spending another.
 */
async function leaseCode(input: OpenDoorInput): Promise<LeaseResult> {
  const apiBase = input.apiBase || '';
  if (!clientToken) clientToken = newClientToken();
  const abort = new AbortController();
  const stall = window.setTimeout(() => abort.abort(), START_TIMEOUT_MS);
  try {
    const res = await fetch(`${apiBase}/api/onboarding/start`, {
      method: 'POST',
      signal: abort.signal,
      credentials: 'include',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        client_token: clientToken,
        surface: input.surface,
        // The page this door is for. The sign-in receipt's link hands the
        // phone the same page this browser lands on after the text arrives.
        return_to: input.returnTo || '/',
        // Where the person is (edge ember/timezone.js): the account is
        // created on the phone, so the browser says it here.
        ...(browserTimezone() ? { timezone: browserTimezone() } : {}),
        // No `name`: no door asks for one. /start still accepts the field and
        // Settings still edits actor.self_name.
        ...(input.tosAcceptedAt
          ? { age_confirmed: true, tos_accepted: true, tos_accepted_at: input.tosAcceptedAt }
          : {}),
        ...(input.extras || {}),
      }),
    });
    const data = (await res.json().catch(() => ({}))) as Record<string, unknown>;
    // The body itself can stall too: past the deadline it is a lost tap, not a door to open.
    if (abort.signal.aborted) throw new DOMException('start timed out', 'AbortError');
    const verification = (data?.verification || {}) as Record<string, unknown>;
    const sessionId = String(data?.onboarding_session_id || '');
    const toNumber = String(verification?.to_number || '');
    // Taken verbatim: /start composes the text, and its exact shape is what the
    // inbound handler matches on, so it has exactly one definition.
    const message = String(verification?.message || '');
    if (!res.ok || !sessionId || !toNumber || !message) {
      const code = String(data?.error || 'handoff_unavailable');
      // The server saw a live session on this request: the page is signed in
      // and does not know it (a completion whose page load never happened).
      // Nothing to send — the session holder re-reads /api/me and the door's
      // page moves on by itself.
      if (code === 'already_signed_in') announceSessionChanged();
      return { ok: false, code };
    }
    const expiresAt = Date.parse(String(verification?.expires_at || ''));
    return {
      ok: true,
      lease: {
        sessionId,
        code: String(verification?.code || ''),
        toNumber,
        message,
        expiresAt: Number.isFinite(expiresAt) ? expiresAt : Date.now() + CODE_TTL_FALLBACK_MS,
        input,
      },
    };
  } catch {
    return { ok: false, code: abort.signal.aborted ? 'network_timeout' : 'network_error' };
  } finally {
    window.clearTimeout(stall);
  }
}

/** Lease the next code for a page still on screen, the ready one about to run out. */
function renewReady(): void {
  const input = prepared?.input;
  if (!input || state.pending) return;
  prepared = null;
  clientToken = newClientToken();
  // The acceptance went with the first lease; the server answers this one from the browser's record.
  void prepareInboundDoor({ ...input, tosAcceptedAt: '' });
}

function scheduleRenewal(lease: LeasedCode): void {
  window.clearTimeout(renewTimer);
  renewTimer = window.setTimeout(() => {
    // A page nobody is looking at leases nothing; the next look does (below).
    if (prepared === lease && document.visibilityState !== 'hidden') renewReady();
  }, Math.max(0, lease.expiresAt - Date.now() - RENEW_AHEAD_MS));
  if (renewArmed || typeof document === 'undefined') return;
  renewArmed = true;
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible' && prepared && prepared.expiresAt - Date.now() < RENEW_AHEAD_MS) renewReady();
  });
}

/**
 * Make a code ready for the tap that is about to come (see `prepared`).
 * Resolves once there is one, or none could be had: a code already ready, or
 * a text already awaited, resolves at once; a lease already on its way is the
 * one waited on. A refusal is recorded and left for the tap, which leases again
 * and says it to the person.
 */
export function prepareInboundDoor(input: OpenDoorInput): Promise<void> {
  if (readyCode(input) || state.pending) return Promise.resolve();
  if (preparing) return preparing.then(() => undefined);
  const asked = leaseCode(input);
  preparing = asked;
  return asked.then((result) => {
    preparing = null;
    if (!result.ok) {
      track('login.failed', { arrival: input.arrival, step: 'prepare', reason: result.code });
      return;
    }
    prepared = result.lease;
    scheduleRenewal(result.lease);
  });
}

/**
 * Hand a leased code to the system SMS composer and take up the wait. Nothing
 * here waits on anything: called inside the person's tap, the `sms:` link is
 * still that tap's.
 */
function handOff(lease: LeasedCode, input: OpenDoorInput, launch: 'in_tap' | 'auto'): OpenDoorResult {
  if (prepared === lease) {
    prepared = null;
    window.clearTimeout(renewTimer);
  }
  const { sessionId, toNumber, message } = lease;
  // Windows and Linux have no app for `sms:`, so there is nothing to jump to:
  // go straight to the phone panel instead of firing a link that silently
  // does nothing. A Mac is not in this branch (getOs → 'mac') — Continuity
  // opens Messages.app, and if it doesn't, the watcher lands on the panel.
  const noSmsApp = getOs() === 'other';
  // …and one environment where firing it is worse than not having an app at
  // all: Instagram on iOS reloads the document instead (ua-detect.ts), which
  // throws away the sheet the person is reading and reads to the watcher as
  // a successful launch. Skip the link there and open on the panel, exactly
  // as a desktop does — the difference is only why (2026-09-17).
  const brokenDeepLink = !noSmsApp && smsDeepLinkUnsupported();
  const skipDeepLink = noSmsApp || brokenDeepLink;
  const record: InboundDoorRecord = {
    sessionId,
    code: lease.code,
    toNumber,
    message,
    returnTo: input.returnTo || '/',
    page: currentPage(),
    arrival: input.arrival,
    surface: input.surface,
    expiresAt: lease.expiresAt,
    apiBase: input.apiBase || '',
    reach: skipDeepLink ? 'phone' : 'pending',
    readyAt: Date.now(),
  };
  // A re-tap gets the same lease back (one client token): it is the same wait, still counting.
  if (state.pending?.sessionId !== sessionId) trace = freshTrace();
  armLifecycle();
  persist(record);
  setState({
    pending: record, arming: false, expired: false, minimized: false, reach: record.reach,
  });
  // The boxes are on file now (the server stamped them when the code was
  // leased), so no door on this browser asks again until the Terms move.
  if (input.tosAcceptedAt) noteVisitorConsent(input.tosAcceptedAt);
  schedule(POLL_MS);
  const smsHref = skipDeepLink ? '' : buildSmsHref(toNumber, message);
  track('login.handoff_ready', {
    arrival: input.arrival,
    // Which shape of sms: link the composer was handed (2026-11-02): the
    // body rides `&body=` on Apple, `?body=` elsewhere, and none when the
    // link was not fired at all.
    sms_style: !smsHref ? 'not_fired' : smsHref.includes('&body=') ? 'amp_body' : smsHref.includes('?body=') ? 'query_body' : 'no_body',
    launch: skipDeepLink ? 'not_fired' : launch,
  });
  if (skipDeepLink) {
    track('login.handoff_fallback', {
      arrival: input.arrival,
      reason: brokenDeepLink ? 'deeplink_unsupported' : 'no_sms_app',
    });
    return { ok: true, reach: 'phone' };
  }
  // Arm the watcher BEFORE navigating, or the backgrounding it listens for
  // happens while there is nothing listening.
  watchReach(input.arrival);
  window.location.href = smsHref;
  return { ok: true, reach: 'pending' };
}

/**
 * The tap: hand the prefilled draft to the system SMS composer. A code made
 * ready for this door (prepareInboundDoor) goes inside the tap itself
 * (launch in_tap). With none ready — the lease that should have readied it
 * failed, or is still on its way — the code is leased now and handed over the
 * moment it is in hand, outside the tap (launch auto).
 *
 * Resolves with what happened; the caller says it to the person. The WAIT is
 * not returned, because it is nobody's component: a successful open hands it to
 * this module, which keeps listening across page loads and finishes the sign-in
 * wherever the person happens to be.
 */
export async function openInboundDoor(input: OpenDoorInput): Promise<OpenDoorResult> {
  const ready = readyCode(input);
  if (ready) return handOff(ready, input, 'in_tap');
  setState({ arming: true });
  const result = await (preparing || leaseCode(input));
  if (!result.ok) {
    setState({ arming: false });
    return result;
  }
  return handOff(result.lease, input, 'auto');
}

/** Abandon the current lease and mint a fresh code — an expired attempt. */
export function restartInboundDoor(input: OpenDoorInput): Promise<OpenDoorResult> {
  track('login.restarted', { arrival: input.arrival });
  clientToken = newClientToken();
  clearReachWatch?.();
  stopPolling();
  setState({ pending: null, expired: false, reach: 'pending' });
  return openInboundDoor(input);
}

/**
 * Hand the SAME leased draft to the composer again. No new lease: the code on
 * screen is the one the inbound handler is waiting for, and minting another
 * would spend a pool number to say the same thing.
 */
export function reopenMessages(): void {
  const record = state.pending;
  if (!record) return;
  // Never re-fire a link this environment is known to break on: on iOS
  // Instagram the "tap again" would reload the page a second time and take
  // the code off screen with it. The surfaces do not offer the button there —
  // this is the floor under that, so a stray caller cannot undo it.
  if (smsDeepLinkUnsupported()) return;
  track('login.handoff_requested', { arrival: record.arrival });
  window.location.href = buildSmsHref(record.toNumber, record.message);
}

/** "It didn't open" — the person asking for the phone panel themselves. */
export function askForPhonePanel(): void {
  const record = state.pending;
  if (!record) return;
  clearReachWatch?.();
  track('login.handoff_fallback', { arrival: record.arrival, reason: 'asked' });
  const next = { ...record, reach: 'phone' as DoorReach };
  persist(next);
  setState({ pending: next, reach: 'phone' });
}

/** Where the QR sends their phone: the same verify session, reopened on a
 *  device that can actually send the text. Both tabs keep polling, so
 *  whichever device sends it, both sign in. */
export function phoneHandoffHref(record: InboundDoorRecord | null): string {
  if (!record || typeof window === 'undefined') return '';
  return `${window.location.origin}/j/${encodeURIComponent(record.sessionId)}`;
}

/** Forget the pending text entirely — a cancelled or superseded attempt. */
export function clearInboundDoor(): void {
  clearReachWatch?.();
  stopPolling();
  persist(null);
  setState({ ...EMPTY });
}

/**
 * The document moved to `pathname` without a page load — an SPA route change.
 * A wait opened on a different page ends here (2026-09-03): the record goes,
 * the poller stops, the pill leaves with them. Landing on the same path again,
 * or a navigation guard holding the route in place, changes nothing. Page
 * loads run the same check inside resumeInboundDoor.
 */
export function endInboundDoorIfLeft(pathname: string): void {
  const record = state.pending;
  if (!record || !leftPage(record, pathname)) return;
  // The same fact "Not now" reports, so the funnel can tell a person who
  // walked away from one whose text never came. An expired wait has already
  // said how it ended (login.handoff_expired).
  if (!state.expired) track('login.failed', { arrival: record.arrival, step: 'wait', reason: 'left_page' });
  clearInboundDoor();
}

/** Close the wait's own surface without ending the wait (or raise it again). */
export function minimizeInboundDoor(minimized: boolean): void {
  // Raising it again is the pill's own tap (ui.clicked login_wait_show).
  if (minimized && state.pending && !state.minimized) trackWait('minimized', state.pending);
  setState({ minimized });
}

/**
 * Pick the wait back up after a page load onto the page it began on — the
 * whole reason the record is stored. Called once per document, in the browser
 * only.
 */
export function resumeInboundDoor(): void {
  if (resumed) return;
  resumed = true;
  const record = readStored();
  if (!record) return;
  // Past the code's deadline by this browser's clock. A dead wait is dropped
  // without a word (2026-08-31): the expired sheet exists to explain a failure
  // to someone watching for the result, not to ambush whoever wanders back to
  // the site later. But the text may have landed while the page was away, and
  // only the server knows (2026-11-02): on the page the door was opened on, it
  // is asked once — signed in carries on as any landing does, anything else is
  // dropped as before. The row says they came back.
  if (record.expiresAt && Date.now() > record.expiresAt) {
    const drop = (): void => { trackWait('resumed_expired', record); persist(null); };
    if (leftPage(record, currentPage())) { drop(); return; }
    void readStatus(record).then((answer) => { if (settleFromStatus(record, answer) !== 'done') drop(); });
    return;
  }
  // Opened on another page: the wait ended the moment they left it
  // (2026-09-03). This load is the first chance to say so — an SPA route
  // change reports the same fact through endInboundDoorIfLeft.
  if (leftPage(record, currentPage())) {
    track('login.failed', { arrival: record.arrival, step: 'wait', reason: 'left_page' });
    persist(null);
    return;
  }
  // Resumed as the pill, never the open sheet: a live wait needs no surface
  // to complete — the poll signs the browser in by itself — and a sheet the
  // person already closed once must not greet every page they open next.
  armLifecycle();
  trackWait('resumed', record);
  setState({ pending: record, expired: false, minimized: true, reach: record.reach });
  // Immediately: the text may well have arrived while the tab was away.
  void poll();
}

/**
 * Stop waiting because the browser is already signed in — the session arrived
 * some other way (another tab finished the same text, a returning cookie).
 */
export function settleInboundDoorIfSignedIn(): void {
  if (state.pending) clearInboundDoor();
  else persist(null);
}

/** Ask again right now (a tab coming back to the foreground). */
export function pokeInboundDoor(): void {
  if (!state.pending || state.expired) return;
  void poll();
}

export function useInboundDoor(): InboundDoorState {
  const [snapshot, setSnapshot] = useState(state);
  useEffect(() => {
    const listener = (): void => setSnapshot(state);
    listeners.add(listener);
    listener();
    return () => { listeners.delete(listener); };
  }, []);
  return snapshot;
}

/** Say what went wrong, in the person's terms. One door, one vocabulary. */
export function inboundDoorFailureMessage(code: string): string {
  if (code === 'pool_empty') return 'We’re at capacity right now — please try again in a few minutes.';
  if (code === 'tos_acceptance_required' || code === 'age_confirmation_required') {
    return 'Please confirm your age and the Terms to continue.';
  }
  if (code === 'network_error') return 'Network error — please try again.';
  if (code === 'network_timeout') return 'Couldn’t reach Ember. Check your connection and try again.';
  if (code === 'already_signed_in') return 'You’re already signed in.';
  // A browser stopped as under 18 (edge age-check.js): no sign-up from it.
  if (code === 'not_served') return 'Ember is only available to adults 18 and older.';
  return 'Couldn’t prepare the message — please try again.';
}

/* --- the consent this browser already gave ------------------------------- */

/**
 * 18-or-older and the Terms are asked once per browser, not once per door.
 *
 * The record has existed since migration 135; nothing read it, so both doors
 * drew the same two checkboxes at people who had ticked them on this same
 * browser an hour earlier. Consent is a fact about the person, not about the
 * tap. The refusal stays server-side (POST /api/onboarding/start and POST
 * /api/conversations both consult the same record), so this read only decides
 * what is DRAWN, and a version bump un-answers it (the endpoint compares
 * against CURRENT_LEGAL_VERSION).
 */
export interface VisitorConsent {
  /** False until the read comes back: nothing about consent is assumed. */
  known: boolean;
  consented: boolean;
  /** ISO stamp of the acceptance on file ('' when there is none). */
  acceptedAt: string;
}

const CONSENT_UNKNOWN: VisitorConsent = { known: false, consented: false, acceptedAt: '' };

let consent: VisitorConsent = CONSENT_UNKNOWN;
let consentInflight: Promise<void> | null = null;
const consentListeners = new Set<() => void>();

function publishConsent(next: VisitorConsent): void {
  consent = next;
  for (const listener of consentListeners) listener();
}

function loadConsent(apiBase: string): Promise<void> {
  if (consentInflight) return consentInflight;
  consentInflight = fetch(`${apiBase}/api/visitor/consent`, {
    credentials: 'include',
    headers: { Accept: 'application/json' },
  })
    .then((res) => (res.ok ? res.json() : null))
    .then((data: { consented?: boolean; accepted_at?: string } | null) => {
      publishConsent({
        known: true,
        consented: data?.consented === true,
        acceptedAt: String(data?.accepted_at || ''),
      });
    })
    .catch(() => {
      // A failed read must fail towards ASKING: the boxes are the safe state,
      // and a door that skipped them on a network error would be collecting
      // nothing while looking as if it had.
      publishConsent({ known: true, consented: false, acceptedAt: '' });
    })
    .finally(() => { consentInflight = null; });
  return consentInflight;
}

/**
 * The moment the person ticked both boxes, now on file — the server records it
 * on the same /start that leases the line. Kept here too so a second door in
 * this same session does not ask again before the next page load.
 */
export function noteVisitorConsent(acceptedAt: string): void {
  publishConsent({ known: true, consented: true, acceptedAt: String(acceptedAt || '') });
}

export function useVisitorConsent(apiBase = ''): VisitorConsent {
  const [snapshot, setSnapshot] = useState(consent);
  useEffect(() => {
    const listener = (): void => setSnapshot(consent);
    consentListeners.add(listener);
    listener();
    if (!consent.known) void loadConsent(apiBase);
    return () => { consentListeners.delete(listener); };
  }, [apiBase]);
  return snapshot;
}

/* --- the browser half of a dual-reported conversion ---------------------- */

/**
 * CompleteRegistration on BOTH ad platforms, each quoting the same
 * server-minted id (`ads_event_id` from GET /api/onboarding/:id/status) so each
 * platform collapses its own browser/server pair instead of counting twice —
 * the dedup contract lives in src/edge/analytics/ads-forwarding.js.
 *
 * None of `fbq`, `ttq` or `rdt` exists unless the consent gate in the host
 * document loaded it, and that absence IS the gate, per platform. Reddit's
 * twin is `SignUp` with `conversionId` — the same id the server's SIGN_UP
 * quotes as conversion_id, so Reddit collapses the pair the way Meta does. No id means no
 * registration to report — a returning sign-in stays silent. Nothing is
 * reported on the handoff tap: that act reaches both platforms server-side
 * (Lead, then Contact), and firing here as well counted one act twice.
 */
export function trackRegistrationConversion(adsEventId: string | undefined): void {
  const id = String(adsEventId || '').trim();
  if (!id) return;
  const w = window as unknown as {
    fbq?: (event: string, name: string, props?: object, opts?: object) => void;
    ttq?: { track?: (name: string, props?: object, opts?: object) => void };
    rdt?: (event: string, name: string, props?: object) => void;
  };
  if (typeof w.fbq === 'function') {
    try {
      w.fbq('track', 'CompleteRegistration', {}, { eventID: id });
    } catch {
      /* a pixel must never hold up the sign-in it is reporting on */
    }
  }
  if (typeof w.ttq?.track === 'function') {
    try {
      w.ttq.track('CompleteRegistration', {}, { event_id: id });
    } catch {
      /* same posture */
    }
  }
  if (typeof w.rdt === 'function') {
    try {
      w.rdt('track', 'SignUp', { conversionId: id });
    } catch {
      /* same posture */
    }
  }
}
